import { Link } from "@tanstack/react-router";
import {
  ArrowUpRight,
  BookOpen,
  CreditCard,
  House,
  Key,
  Users,
  Webhook,
} from "lucide-react";
import sullyWordmark from "@/assets/sully-wordmark.png";
import ProfileDropdown from "@/components/common/ProfileDropdown";
import { OrganizationDropdown } from "@/components/organization/OrganizationDropdown";
import { ROUTES } from "@/constants/routeConstants";
import { useOrganizationStore } from "@/store/organization";

interface NavItemProps {
  to: string;
  icon: React.ElementType;
  label: string;
}

const navItemClassName =
  "flex min-h-11 items-center gap-2.5 whitespace-nowrap rounded-lg border px-2.5 text-[13px] font-medium transition-colors md:w-full";

function NavItem({ to, icon: Icon, label }: NavItemProps) {
  return (
    <Link
      to={to}
      activeProps={{
        className: "border-border bg-background text-foreground",
      }}
      inactiveProps={{
        className:
          "border-transparent text-muted-foreground hover:bg-background hover:text-foreground",
      }}
      className={navItemClassName}
    >
      <Icon className="size-4 shrink-0" />
      <span>{label}</span>
    </Link>
  );
}

function DocumentationNavItem() {
  return (
    <a
      href="https://docs.sully.ai"
      target="_blank"
      rel="noopener noreferrer"
      className={`${navItemClassName} border-transparent text-muted-foreground hover:bg-background hover:text-foreground`}
    >
      <BookOpen className="size-4 shrink-0" />
      <span>Documentation</span>
      <ArrowUpRight aria-hidden="true" className="ml-auto size-4 shrink-0" />
      <span className="sr-only"> (opens in a new tab)</span>
    </a>
  );
}

/** Organization-aware navigation with admin-only Billing and external docs access. */
export function Sidebar() {
  const organization = useOrganizationStore((state) => state.organization);
  return (
    <aside aria-label="Application sidebar" className="dashboard-sidebar">
      <div className="sidebar-brand">
        <span className="sidebar-wordmark">
          <img src={sullyWordmark} alt="Sully" />
        </span>
        <span className="whitespace-nowrap text-base font-medium text-foreground">
          API Console
        </span>
      </div>
      <div className="sidebar-organization">
        <OrganizationDropdown />
      </div>

      <nav aria-label="Main navigation" className="sidebar-navigation">
        <NavItem to={ROUTES.ANALYTICS} icon={House} label="Dashboard" />
        <NavItem to={ROUTES.API_KEYS} icon={Key} label="API Keys" />
        <NavItem to="/webhooks" icon={Webhook} label="Webhooks" />
        <NavItem to="/members" icon={Users} label="Members" />
        {organization?.role === "admin" && (
          <NavItem to={ROUTES.BILLING} icon={CreditCard} label="Billing" />
        )}
        <DocumentationNavItem />
      </nav>

      <div className="sidebar-account">
        <ProfileDropdown />
      </div>
    </aside>
  );
}
